import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Field } from 'formik';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import faPlus from '@fortawesome/fontawesome-free-solid/faPlus';

import SelectInput from '../../../../common/form/SelectInput';
import itemSearchAttributes from '../../../../../config/itemSearchAttributes';
import { querySubcategories, queryStockSubcategories } from '../../../../../redux/ducks/items';
import { Attributes } from '../../../../../config/dataAttributesIndex';
import { ItemType } from '../../../../../config/constants';
import Button from '../../../../common/Button';
import itemDefault from '../../../../../images/item-default.png';

import './ItemDrillDown.css';

const optionGenerator = (v, k) => ({ label: k.toString(), value: k.toString() });

class ItemDrillDown extends Component {
  constructor(props) {
    super(props);

    this.state = {
      categoryLevel: props.type,
      secondaryLevels: [],
    };
  }

  componentDidMount() {
    this.props.onQuerySubcategories([this.props.type], false);
  }

  isTerminalQuery = () => {
    const { categoryLevel, secondaryLevels } = this.state;
    const searchAttributes = itemSearchAttributes[categoryLevel];

    return secondaryLevels.length === searchAttributes.length;
  };

  renderSubcategories = () => {
    const { categoryLevel, secondaryLevels } = this.state;

    const {
      stockDataMap, isFetchingStockData, onQuerySubcategories,
      onQueryStockSubcategories, formValueSetter,
    } = this.props;

    if (!categoryLevel) {
      return null;
    }

    const searchAttributes = itemSearchAttributes[categoryLevel];
    const allAttributes = Attributes[categoryLevel];

    const levelOptions = [
      stockDataMap[categoryLevel],
      ...secondaryLevels.map((level, index) =>
        _.get(stockDataMap, [categoryLevel, ...secondaryLevels.slice(0, index + 1)])),
    ];

    const fields = searchAttributes.map((attr, levelIndex) => (
      <Field
        key={attr}
        name={`specificData[${attr}]`}
        label={allAttributes[attr].friendly}
        options={levelOptions[levelIndex] ? _.map(levelOptions[levelIndex], optionGenerator) : []}
        isLoading={isFetchingStockData}
        component={SelectInput}
        changeEnhancer={(e) => {
          const {
            target: { value },
          } = e;

          // Ask for more details
          onQuerySubcategories(
            [categoryLevel, ...secondaryLevels.slice(0, levelIndex), value],
            levelIndex + 1 === searchAttributes.length
          );

          onQueryStockSubcategories([categoryLevel, ...secondaryLevels.slice(0, levelIndex),
            value], levelIndex + 1 === searchAttributes.length);

          // Update the selected values
          this.setState((prevState) => {
            return levelIndex === 0
              ? { secondaryLevels: [value] }
              : {
                  secondaryLevels: [...prevState.secondaryLevels.slice(0, levelIndex), value],
                };
          });
          // Clear down-tree options from form model
          searchAttributes.slice(levelIndex + 1).forEach((searchAttr) => {
            formValueSetter(`specificData[${searchAttr}]`, '', false);
          });
        }}
      />
    ));

    return <div className="column">{fields}</div>;
  };

  renderItemPreviews = () => {
    const { categoryLevel, secondaryLevels } = this.state;
    const { searchDataMap, onPickItem, isWriting } = this.props;

    if (!this.isTerminalQuery() && secondaryLevels.length === 0) {
      return null;
    }

    const matchingItems = _.get(searchDataMap, [categoryLevel, ...secondaryLevels]);

    if (!matchingItems || matchingItems.length === 0) {
      return null;
    }

    return (
      <div className="column">
        <p className="label">Is your item one of the following?</p>

        <ul className="stockItemPreview">
          {_.map(matchingItems, item => (
            <li className="stockItemPreview-box" key={item.id}>
              <img
                src={item.stockImage == null ? itemDefault : item.stockImage.thumbnailPath}
                alt={item.data ? item.data.w_name || item.data.c_name || item.data.comic_name : ''}
                className="stockItemPreview-thumb"
              />
              <div className="stockItemPreview-details">

                <div className="stockItemPreview-year">{item.data.w_brand || item.data.c_year || item.data.comic_name}</div>
                <div className="stockItemPreview-brand">{item.data.w_name || item.data.c_brand || item.data.comic_publisher}</div>
                <div className="stockItemPreview-subInfo">{item.data.c_sub_model} {item.data.c_body_style}</div>
                {/* <div className="stockItemPreview-meta">Details here...</div> */}

              </div>
              <div className="stockItemPreview-pickButton">
                <Button
                  label={
                    <React.Fragment>
                      <FontAwesomeIcon icon={faPlus} className="icon" size="sm" />{' '}
                      <span>Add This</span>
                    </React.Fragment>
                  }
                  handler={() => {
                    onPickItem(item, categoryLevel);
                  }}
                  size="small"
                  color="link"
                  isActive={isWriting}
                  isDisabled={isWriting}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  render() {
    const { categoryLevel } = this.state;
    const { onChangeStep, onPickItem, isWriting } = this.props;

    return (
      <div>
        <h2 className="title is-2 level">
          Add a new {ItemType[this.props.type]}{' '}
          <Button
            label="Back"
            color="link"
            size="small"
            className="is-outlined"
            handler={() => {
              onChangeStep(1);
            }}
          />
        </h2>

        <div className="columns">
          {this.renderSubcategories()}
          {this.renderItemPreviews()}
        </div>

        <div className="has-text-centered">
          <Button
            label="I don't see mine here"
            color="info"
            isActive={isWriting}
            isDisabled={isWriting}
            handler={() => {
              onPickItem({}, categoryLevel);
            }}
            className="is-outlined"
          />
        </div>
      </div>
    );
  }
}

ItemDrillDown.propTypes = {
  type: PropTypes.oneOf(_.keys(ItemType)).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  stockDataMap: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  searchDataMap: PropTypes.object.isRequired,
  isFetchingStockData: PropTypes.bool.isRequired,
  onQuerySubcategories: PropTypes.func.isRequired,
  onQueryStockSubcategories: PropTypes.func.isRequired,
  onChangeStep: PropTypes.func.isRequired,
  isWriting: PropTypes.bool.isRequired,
  onPickItem: PropTypes.func.isRequired,
  formValueSetter: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  const {
    items: { stockDataMap, searchDataMap, isFetchingStockData },
  } = state;

  return {
    stockDataMap,
    searchDataMap,
    isFetchingStockData,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onQuerySubcategories: (path, isTerminal) => {
      dispatch(querySubcategories(path, isTerminal));
    },
    onQueryStockSubcategories: (path, isTerminal) => {
      dispatch(queryStockSubcategories(path, isTerminal));
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ItemDrillDown);



// WEBPACK FOOTER //
// ./src/components/pages/collections/items/addForms/ItemDrillDown.jsx